import { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { LICENSE_CONCLUDED } from '../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../shared/Locator';
import { emptyConclusionData } from '../../../../../shared/types/licenses';
import { Action } from '../../../../../typings/permissions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useProjectCorrection } from '../../../../hooks/api';
import { useNoticeFiles, useRevision } from '../../../../hooks/api/revisions';
import { getQueryKey as getRevisionQueryKey } from '../../../../hooks/api/revisions/useRevision';
import { queryClient } from '../../../../ReactQuery';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import useHasProjectPermission from '../../../../shared/permissions/useHasProjectPermission';
import { Icon, icons } from '../../../generic';
import Button, { BUTTON_SIZE, ButtonGroup, TooltipButton } from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import { useFromNow } from '../../../generic/FromNow';
import Tabs from '../../../generic/Tabs';
import ModalEditDependency from '../../../modals/ModalEditDependency';
import ModalDeleteDependency from '../../dependencies/ModalDeleteDependency';
import type { Dependency } from '../../../../../shared/dependenciesV2';

import DependencyLicenses from './content/DependencyLicenses/DependencyLicenses';
import DependencyMatchDetails from './content/DependencyMatchDetails';
import { DependencyAuthors } from './DependencyAuthors/DependencyAuthors';
import DependencyDetailsContainer from './DependencyDetails/DependencyDetailsContainer';
import { DependencyLabels } from './DependencyLabels/DependencyLabels';
import { DependencyNoticeContainer } from './DependencyNotices/DependencyNoticeContainer';
import { DependencyPaths } from './DependencyPaths/DependencyPaths';
import type { LicenseTabScope } from './content/DependencyLicenses/types';
import type { DrawerScope } from './types';

import styles from './DependencyDrawer.module.less';

const DeleteCorrectionConfirmationBody = () => (
  <Flex direction="column" gap={12} justify="flex-start" align="flex-start">
    <span>
      Are you sure you want to delete the corrections to this dependency across all projects and release groups?
    </span>
    <div>
      <span>This will remove all corrections to:</span>
      <ul>
        <li>Dependency metadata (title, description, URL, authors)</li>
        <li>NOTICE files</li>
        <li>Licenses</li>
        <li>License conclusions</li>
      </ul>
    </div>
  </Flex>
);

type DependencyDrawerTabs = 'details' | 'matches' | 'licenses' | 'paths' | 'labels' | 'authors' | 'notice';

type Props = {
  dependency: Dependency;
  scope: DrawerScope;
  onEditDependency: () => void;
  onDeleteDependency?: () => void;
  onClose: () => void;
};

const DependencyContent: VoidFunctionComponent<Props> = ({
  dependency,
  scope,
  onEditDependency,
  onDeleteDependency,
  onClose,
}) => {
  const dialogContext = useDialogContext();
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<DependencyDrawerTabs>('details');
  const [isEditDependencyModalOpen, setIsEditDependencyModalOpen] = useState(false);
  const [isDeleteDependencyModalOpen, setIsDeleteDependencyModalOpen] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  // In order to determine if a dependency has been corrected, we need to check the revision + project for correction
  // fields, as well as the notice files.
  const { data: revision, isLoading: isRevisionLoading } = useRevision(dependency.locator, true);
  const {
    data: noticeFiles,
    isLoading: isNoticeFilesLoading,
    isError: isNoticeFilesError,
  } = useNoticeFiles(dependency.locator);

  const { deleteCorrection, rebuild } = useProjectCorrection(
    revision?.locator as string,
    scope.type === 'project' ? scope.revisionLocator : undefined,
  );

  const hasCorrection =
    (revision as { corrected?: boolean })?.corrected ||
    // @ts-expect-error projectCorrection really does exist on this response.
    !!revision?.project?.projectCorrection ||
    (noticeFiles && noticeFiles.length > 0 && noticeFiles.some((notice) => notice.corrected));

  const dependencyCacheUpdatedFromNow = useFromNow(revision?.dependency_cache_updated);

  const isBinaryDependency = Locator.parse(dependency.locator).fetcher === 'csbinary';
  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);

  const licenseTabScope: LicenseTabScope =
    scope.type === 'global'
      ? {
          dependencyRevisionLocator: dependency.locator,
          concludedLicenseCountMap: {},
          ...scope,
        }
      : {
          dependencyRevisionLocator: dependency.locator,
          ...scope,
        };

  const onDeleteCorrection = () => {
    dialogContext.onConfirm({
      title: 'Delete dependency correction',
      body: <DeleteCorrectionConfirmationBody />,
      onConfirm() {
        deleteCorrection.mutate(undefined, {
          onSuccess() {
            addToast({
              type: 'success',
              content: 'Dependency corrections have been deleted',
            });
            queryClient.invalidateQueries({
              // Wildcard invalidation for the dependency in both the project and release group scopes.
              predicate: (query) => {
                const key = query.queryKey;
                return key.includes('dependencies') && key.includes(dependency.locator);
              },
            });
            // Invalidate the revision query key to ensure the corrections are re-fetched.
            queryClient.invalidateQueries({
              queryKey: getRevisionQueryKey(dependency.locator),
            });
            dispatch(DependenciesV2Store.actions.preFetchLoading());
            dispatch(DependenciesV2Store.actions.fetchDependencies());
          },
          onError() {
            addToast({
              type: 'error',
              content: `Unable to delete dependency correction`,
            });
          },
        });
      },
    });
  };

  const isSuperUser = window.user?.super;
  const hasPermission = useHasProjectPermission(Locator.parsePackageString(dependency.locator), Action.Edit);
  const canRebuild = revision?.project.organizationId ? hasPermission : true;

  return (
    <>
      <div className={styles.dependencyContent}>
        <div className={styles.title}>
          {dependency.title}
          <span className={styles.version}>{dependency.version ?? Locator.parse(dependency.locator).revision}</span>
        </div>
        <div>
          <ButtonGroup>
            {!isLicenseConcludedEnabled && (
              <Button type="secondary" onClick={() => setIsEditDependencyModalOpen(true)}>
                Edit
              </Button>
            )}
            {scope.type === 'project' && (
              // Only show the delete/ignore button for project scope.
              <Button type="secondary" size={BUTTON_SIZE.MEDIUM} onClick={() => setIsDeleteDependencyModalOpen(true)}>
                {dependency.isManual ? 'Delete' : 'Ignore'}
              </Button>
            )}
            {isLicenseConcludedEnabled && (
              <TooltipButton
                id="reanalyze-button-tooltip"
                // If the value is null, we want to hide the timestamp.
                showTooltip={!!revision?.dependency_cache_updated}
                // Show the exact timestamp to super users for debugging purposes, since it's not shown anywhere else in the
                // UI.
                tooltip={`Last analyzed: ${
                  isSuperUser ? revision?.dependency_cache_updated : dependencyCacheUpdatedFromNow
                }`}
                type="secondary"
                size={BUTTON_SIZE.MEDIUM}
                isDisabled={isRebuilding || !canRebuild}
                onClick={() => {
                  rebuild.mutate();
                  setIsRebuilding(true);
                  addToast({
                    type: 'success',
                    content: 'Dependency is being reanalyzed.',
                  });
                }}
              >
                {isRebuilding ? <Icon icon={icons.checkOutline} /> : <Icon icon={icons.reload} />} Reanalyze
              </TooltipButton>
            )}
            {isLicenseConcludedEnabled && hasCorrection && (
              <Button type="error" size={BUTTON_SIZE.MEDIUM} onClick={onDeleteCorrection} isDisabled={isRevisionLoading}>
                Delete Correction
              </Button>
            )}
          </ButtonGroup>
        </div>
        <div className={styles.dependencyContentContainer}>
          <Tabs>
            <Tabs.Tab
              label="Details"
              key="details"
              isActive={activeTab === 'details'}
              onSelect={() => setActiveTab('details')}
            />
            {isBinaryDependency && (
              <Tabs.Tab
                label="Matches"
                key="matches"
                isActive={activeTab === 'matches'}
                onSelect={() => setActiveTab('matches')}
              />
            )}
            {isLicenseConcludedEnabled && (
              <Tabs.Tab
                label="Licenses"
                key="licenses"
                isActive={activeTab === 'licenses'}
                onSelect={() => setActiveTab('licenses')}
              />
            )}
            {isLicenseConcludedEnabled && scope.type === 'project' && (
              <Tabs.Tab
                label="Paths"
                key="paths"
                isActive={activeTab === 'paths'}
                onSelect={() => setActiveTab('paths')}
              />
            )}
            {isLicenseConcludedEnabled && scope.type === 'project' && (
              <Tabs.Tab
                label="Labels"
                key="labels"
                isActive={activeTab === 'labels'}
                onSelect={() => setActiveTab('labels')}
              />
            )}
            {isLicenseConcludedEnabled && (
              <Tabs.Tab
                label="Authors"
                key="authors"
                isActive={activeTab === 'authors'}
                onSelect={() => setActiveTab('authors')}
              />
            )}
            {isLicenseConcludedEnabled && (
              <Tabs.Tab
                label="NOTICE"
                key="notice"
                isActive={activeTab === 'notice'}
                onSelect={() => setActiveTab('notice')}
              />
            )}
          </Tabs>
          {activeTab === 'details' && <DependencyDetailsContainer dependency={dependency} />}
          {activeTab === 'matches' && isBinaryDependency && <DependencyMatchDetails dependency={dependency} />}
          {activeTab === 'licenses' && isLicenseConcludedEnabled && (
            <DependencyLicenses
              issues={dependency.issues}
              licenseGroups={dependency.licenseGroups}
              concludedLicenses={dependency.conclusions || emptyConclusionData}
              scope={licenseTabScope}
              isLoading={false}
              dependency={{
                title: dependency.title,
                version: dependency.version,
                locator: dependency.locator,
              }}
              shouldAllowEditing
            />
          )}
          {activeTab === 'paths' && isLicenseConcludedEnabled && scope.type === 'project' && (
            <DependencyPaths
              rootProjectLocator={scope.revisionLocator}
              dependencyLocator={dependency.locator}
              dependencyTitle={dependency.title}
            />
          )}
          {activeTab === 'labels' && isLicenseConcludedEnabled && scope.type === 'project' && (
            <DependencyLabels
              dependencyLocator={dependency.locator}
              dependencyTitle={dependency.title}
              parentRevisionLocator={scope.revisionLocator}
            />
          )}
          {activeTab === 'authors' && isLicenseConcludedEnabled && <DependencyAuthors dependency={dependency} />}
          {activeTab === 'notice' && isLicenseConcludedEnabled && (
            <DependencyNoticeContainer
              dependency={dependency}
              notices={noticeFiles}
              isLoading={isNoticeFilesLoading}
              isError={isNoticeFilesError}
            />
          )}
        </div>
      </div>
      <ModalEditDependency
        show={isEditDependencyModalOpen}
        onHide={() => setIsEditDependencyModalOpen(false)}
        onChange={() => {
          onEditDependency();
          setIsEditDependencyModalOpen(false);
        }}
        revisionLocator={dependency?.locator}
        parentRevisionLocator={dependency?.rootProjects.length === 1 ? dependency.rootProjects[0]?.revision : undefined}
      />
      {scope.type === 'project' && (
        <ModalDeleteDependency
          show={isDeleteDependencyModalOpen}
          onHide={() => setIsDeleteDependencyModalOpen(false)}
          onDeleteDependency={() => {
            onDeleteDependency?.();
            setIsDeleteDependencyModalOpen(false);
          }}
          onIgnoreDependency={() => {
            onDeleteDependency?.();
            setIsDeleteDependencyModalOpen(false);
          }}
          parent_locator={dependency.rootProjects[0]?.revision}
          onProjectChange={onClose}
          revision={{
            locator: dependency.locator,
            project: {
              title: dependency.title,
            },
            manual: dependency.isManual,
          }}
        />
      )}
    </>
  );
};

export default DependencyContent;
